import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PhotoIcon from '@mui/icons-material/PhotoCamera';
import Alert from '@mui/material/Alert';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { RootState } from '../../store';
import tasksService, { Task, TaskDispatchWorker } from '../../services/api/tasksService';
import inspectionsService, { InspectionRow } from '../../services/api/inspectionsService';
import teamService, { TeamListItem } from '../../services/api/teamService';
import userService, { UserListItem } from '../../services/api/userService';
import timesheetService, { Worker } from '../../services/api/timesheetService';
import attendanceService, { AttendanceRecord } from '../../services/api/attendanceService';
import readFirstRegisterService from '../../services/api/readFirstRegisterService';
import dailyDispatchService from '../../services/api/dailyDispatchService';
import { enqueueOfflineFieldEvidence } from '../../services/offline/fieldOfflineForms';
import { photoEvidencePayload } from '../../services/offline/fieldPhotoCapture';
import fieldSyncService from '../../services/offline/fieldSyncService';
import { submitInspectionVerdict } from './supervisorInspectionActions';
import { taskAwaitingInspection, toLocalIsoDate } from './supervisorFieldUtils';
import { lifecycleLabels, taskTeamIds } from './workerFieldModeUtils';
import { ACCOUNTS_PERSONNEL_QUERY_KEY, FieldPersonnel, fieldPersonnelById, supervisorFieldPersonnel } from '../../routes/fieldPersonnel';
import PersonnelFolderCard from '../../components/field/PersonnelFolderCard';
import SitePlanMap from '../../components/siteplan/SitePlanMap';
import { supervisorSpecificationItems } from '../../utils/taskRoleSpecifications';

type SupervisorView = 'home' | 'current' | 'upcoming' | 'qc' | 'emergency' | 'inspections';

interface SupervisorFieldModePageProps {
  view: SupervisorView;
}

type DispatchSelectedWorker = {
  id?: string | null;
  name?: string | null;
  badgeCode?: string | null;
  teamId?: string | null;
  trade?: string | null;
  selectionType?: string | null;
  lifecycleStatus?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  actualSeconds?: number | string | null;
};

type ProgressWorker = Worker & {
  lifecycleStatus?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  actualSeconds?: number | string | null;
};

const dispatchWorkerStatus = (worker: Pick<TaskDispatchWorker, 'lifecycleStatus'>) =>
  String(worker.lifecycleStatus || 'ready').toUpperCase();

const dispatchWorkerSubmitted = (worker: Pick<TaskDispatchWorker, 'lifecycleStatus'>) =>
  ['INSPECTION_PENDING', 'INSPECTION_PASSED'].includes(dispatchWorkerStatus(worker));

const allSelectedWorkersSubmitted = (task: Task): boolean => {
  const workers = task.dispatchWorkers ?? [];
  return workers.length === 0 || workers.every(dispatchWorkerSubmitted);
};

const taskReadyForSupervisorInspection = (task: Task): boolean =>
  taskAwaitingInspection(task) && allSelectedWorkersSubmitted(task);

const workerElapsedSeconds = (worker: Pick<TaskDispatchWorker, 'actualSeconds' | 'startedAt' | 'completedAt' | 'lifecycleStatus'>, now = Date.now()) => {
  const actualSeconds = Math.max(0, Number(worker.actualSeconds ?? 0) || 0);
  const status = dispatchWorkerStatus(worker);
  const startedAt = worker.startedAt ? new Date(worker.startedAt).getTime() : NaN;
  const completedAt = worker.completedAt ? new Date(worker.completedAt).getTime() : NaN;
  if (Number.isFinite(startedAt) && status === 'IN_PROGRESS') {
    return actualSeconds + Math.max(0, Math.floor((now - startedAt) / 1000));
  }
  if (actualSeconds > 0) return actualSeconds;
  if (Number.isFinite(startedAt) && Number.isFinite(completedAt)) {
    return Math.max(0, Math.floor((completedAt - startedAt) / 1000));
  }
  return 0;
};

const workerProgressPercent = (worker: Pick<TaskDispatchWorker, 'actualSeconds' | 'startedAt' | 'completedAt' | 'lifecycleStatus'>, task: Task, now = Date.now()) => {
  const status = dispatchWorkerStatus(worker);
  if (status === 'INSPECTION_PASSED') return 100;
  if (status === 'INSPECTION_PENDING') return 35;
  if (status !== 'IN_PROGRESS') return 0;
  const plannedSeconds = plannedSecondsFor(task);
  if (plannedSeconds <= 0) return 1;
  return Math.max(1, Math.min(100, Math.round((workerElapsedSeconds(worker, now) / plannedSeconds) * 100)));
};

const todayLabel = () => new Date().toLocaleDateString([], {
  weekday: 'long',
  month: 'long',
  day: 'numeric',
});

const todayIso = () => new Date().toISOString().slice(0, 10);

const formatDate = (value?: string | null) => {
  if (!value) return 'Not scheduled';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const taskLocation = (task: Task): string =>
  task.workLocationRef?.name ||
  task.workLocation ||
  task.component?.unit?.code ||
  task.component?.name ||
  'Site';

const statusKey = (task: Task) => String(task.lifecycleStatus || task.status || '').toUpperCase();

/** Inspection detail for tasks awaiting a verdict; full Task Sheet otherwise. */
const taskLinkFor = (task: Task): string =>
  taskReadyForSupervisorInspection(task) ? `/field/inspections/${task.id}` : `/field/tasks/${task.id}`;

const inspectionProgress = (task?: Task | null) => {
  if (!task) return 0;
  const estimated = Number(task.estimatedHours ?? 0);
  const actual = Number(task.actualHours ?? 0);
  if (estimated > 0 && actual > 0) return Math.max(5, Math.min(100, Math.round((actual / estimated) * 100)));
  const status = statusKey(task);
  if (status === 'IN_PROGRESS') return 1;
  if (status === 'INSPECTION_PENDING') return 35;
  if (status === 'INSPECTION_PASSED') return 100;
  return 0;
};

const formatElapsed = (totalSeconds: number) => {
  const safeSeconds = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = safeSeconds % 60;
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    : `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const plannedSecondsFor = (task?: Task | null) =>
  Math.max(0, Math.round(Number(task?.estimatedHours ?? 0) * 3600));

const elapsedSecondsFor = (task?: Task | null, now = Date.now()) => {
  if (!task) return 0;
  const actualSeconds = Math.max(0, Math.round(Number(task.actualHours ?? 0) * 3600));
  const startedAt = task.actualStartDate ? new Date(task.actualStartDate).getTime() : null;
  const completedAt = task.completionDate ? new Date(task.completionDate).getTime() : null;
  if (startedAt && Number.isFinite(startedAt) && statusKey(task) === 'IN_PROGRESS') {
    return actualSeconds + Math.max(0, Math.floor((now - startedAt) / 1000));
  }
  if (actualSeconds > 0) return actualSeconds;
  if (startedAt && Number.isFinite(startedAt) && completedAt && Number.isFinite(completedAt)) {
    return Math.max(0, Math.floor((completedAt - startedAt) / 1000));
  }
  return 0;
};

const remainingSecondsFor = (task?: Task | null, now = Date.now()) =>
  Math.max(0, plannedSecondsFor(task) - elapsedSecondsFor(task, now));

const timeProgressFor = (task?: Task | null, now = Date.now()) => {
  if (!task) return 0;
  const plannedSeconds = plannedSecondsFor(task);
  if (plannedSeconds <= 0) return inspectionProgress(task);
  const progress = Math.round((elapsedSecondsFor(task, now) / plannedSeconds) * 100);
  return Math.max(statusKey(task) === 'IN_PROGRESS' ? 1 : 0, Math.min(100, progress));
};

const statusLabelFor = (task?: Task | null) => {
  const status = task ? statusKey(task) : 'DRAFT';
  return lifecycleLabels[status] ?? status.replace(/_/g, ' ');
};

const inspectionSortTime = (task: Task) => {
  const value = task.completionDate ?? task.updatedAt ?? task.dueDate ?? task.startDate ?? '';
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : 0;
};

const supervisorCurrentStatuses = new Set(['DRAFT', 'SCHEDULED', 'WAITING', 'READY', 'IN_PROGRESS', 'PAUSED', 'BLOCKED', 'INSPECTION_PENDING']);
const SUPERVISOR_PASSWORD_HINT = 'Password: Super@123';

const supervisorUserToPersonnel = (row: UserListItem): FieldPersonnel => ({
  id: row.id,
  name: `${row.firstName} ${row.lastName}`.trim() || row.email,
  roleLabel: 'Supervisor',
  email: row.email,
  credentialHint: SUPERVISOR_PASSWORD_HINT,
  team: 'Supervisor Team',
  telegramStatus: row.telegramLinkedAt || row.telegramChatId ? 'Telegram linked' : 'Telegram not linked',
});

const mergeSupervisorPersonnel = (rows: UserListItem[]): FieldPersonnel[] => {
  const byEmail = new Map<string, FieldPersonnel>();
  supervisorFieldPersonnel.forEach((person) => byEmail.set(person.email.toLowerCase(), person));
  rows.forEach((row) => {
    const emailKey = row.email.toLowerCase();
    const seeded = byEmail.get(emailKey);
    byEmail.set(emailKey, {
      ...(seeded ?? supervisorUserToPersonnel(row)),
      id: row.id,
      name: `${row.firstName} ${row.lastName}`.trim() || seeded?.name || row.email,
      email: row.email,
      telegramStatus: row.telegramLinkedAt || row.telegramChatId ? 'Telegram linked' : seeded?.telegramStatus ?? 'Telegram not linked',
    });
  });
  return [...byEmail.values()].sort((a, b) => a.name.localeCompare(b.name));
};

const currentInspectionFrom = (
  tasks: Task[],
  user?: { id?: string | null; email?: string | null } | null,
  selectedPersonnel?: { id?: string; email?: string } | null,
) =>
  [...tasks]
    .filter((task) => taskIssuedToSupervisor(task, user, selectedPersonnel))
    .filter(taskReadyForSupervisorInspection)
    .sort((a, b) => {
      const statusRank = (status: string) => status === 'INSPECTION_PENDING' ? 0 : 1;
      return statusRank(statusKey(a)) - statusRank(statusKey(b)) || inspectionSortTime(b) - inspectionSortTime(a);
    })[0] ?? null;

const handoffSortTime = (task: Task) => {
  const value = task.pmIssuedAt ?? task.updatedAt ?? task.dueDate ?? task.startDate ?? '';
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : 0;
};

const assignedCrewIdFor = (task: Task): string | null =>
  task.teamId
  ?? task.teamAssignments?.find((assignment) => assignment.teamId || assignment.team_id)?.teamId
  ?? task.teamAssignments?.find((assignment) => assignment.teamId || assignment.team_id)?.team_id
  ?? null;

const assignedCrewNamesFor = (task: Task): string[] => [
  task.team?.name,
  ...(task.teamAssignments ?? [])
    .filter((assignment) => !assignment.lifecycleStatus || !['CANCELLED', 'REMOVED'].includes(String(assignment.lifecycleStatus).toUpperCase()))
    .map((assignment) => assignment.team?.name)
    .filter(Boolean),
].filter((value, index, list): value is string => Boolean(value) && list.indexOf(value) === index);

const normalizedPersonName = (first?: string | null, last?: string | null) =>
  `${first ?? ''} ${last ?? ''}`.trim().replace(/\s+/g, ' ').toLowerCase();

const normalizedWorkerName = (worker: Worker) => worker.name.trim().replace(/\s+/g, ' ').toLowerCase();
const isExecutionWorker = (worker: Worker) => {
  const teamType = String(worker.team?.teamType ?? '').toLowerCase();
  if (teamType) return ['construction', 'subcontractor'].includes(teamType);
  return !/stores|accounts|procurement|sales|supervisor/i.test(worker.team?.name ?? '');
};

const taskIssuedToSupervisor = (
  task: Task,
  user?: { id?: string | null; email?: string | null } | null,
  selectedPersonnel?: { id?: string; email?: string } | null,
) => {
  if (!task.pmIssuedToSupervisorId && !task.pmIssuedToSupervisor?.email) return false;
  const targetEmail = (selectedPersonnel?.email ?? user?.email ?? '').toLowerCase();
  if (targetEmail && task.pmIssuedToSupervisor?.email?.toLowerCase() === targetEmail) return true;
  if (selectedPersonnel?.id && task.pmIssuedToSupervisorId === selectedPersonnel.id) return true;
  return Boolean(user?.id && task.pmIssuedToSupervisorId === user.id);
};

const currentHandoffFrom = (
  tasks: Task[],
  user?: { id?: string | null; email?: string | null } | null,
  selectedPersonnel?: { id?: string; email?: string } | null,
) =>
  [...tasks]
    .filter((task) => taskIssuedToSupervisor(task, user, selectedPersonnel))
    .filter((task) => supervisorCurrentStatuses.has(statusKey(task)))
    .sort((a, b) => handoffSortTime(b) - handoffSortTime(a))[0] ?? null;

const latestPmHandoffFrom = (tasks: Task[]) =>
  [...tasks]
    .filter((task) => Boolean(task.pmIssuedToSupervisorId || task.pmIssuedToSupervisor?.email))
    .filter((task) => supervisorCurrentStatuses.has(statusKey(task)))
    .sort((a, b) => handoffSortTime(b) - handoffSortTime(a))[0] ?? null;

const currentSupervisorTaskFrom = (
  tasks: Task[],
  user?: { id?: string | null; email?: string | null } | null,
  selectedPersonnel?: { id?: string; email?: string } | null,
) => currentInspectionFrom(tasks, user, selectedPersonnel)
  ?? currentHandoffFrom(tasks, user, selectedPersonnel)
  ?? latestPmHandoffFrom(tasks);

const upcomingFrom = (tasks: Task[], currentTaskId?: string) =>
  tasks
    .filter((task) => task.id !== currentTaskId)
    .filter((task) => !['INSPECTION_PASSED', 'CLOSED', 'CANCELLED'].includes(statusKey(task)))
    .sort((a, b) => String(a.dueDate ?? a.startDate ?? '').localeCompare(String(b.dueDate ?? b.startDate ?? '')));

const formatTime = (value?: string | Date | null) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatDateTime = (value?: string | Date | null) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const completedByNameFor = (task?: Task | null) => {
  const completedWorkers = task?.completedDispatchWorkers ?? [];
  const names = completedWorkers
    .map((worker) => worker.name || worker.userEmail)
    .filter(Boolean);
  if (names.length > 0) return names.join(', ');
  return task?.completedByName ?? task?.completedBy?.name ?? task?.completedBy?.email ?? 'Worker not recorded';
};

function PageTitle({ title, projectName }: { title: string; projectName: string }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 900, textTransform: 'uppercase' }}>{title}</Typography>
        <Typography variant="h4" sx={{ fontSize: 30, fontWeight: 900, letterSpacing: 0 }}>Supervisor</Typography>
        <Typography color="text.secondary">{todayLabel()} - {projectName}</Typography>
      </Box>
      <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: 'primary.main' }} />
    </Stack>
  );
}

function MetricCard({ label, value, detail }: { label: string; value: string | number; detail: string }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1, bgcolor: '#fff', minHeight: 104 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 900, textTransform: 'uppercase' }}>{label}</Typography>
      <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1.1 }}>{value}</Typography>
      <Typography variant="body2" color="text.secondary">{detail}</Typography>
    </Paper>
  );
}

function MenuBar({ to, label, detail }: { to: string; label: string; detail: string }) {
  const [searchParams] = useSearchParams();
  const view = searchParams.get('view');
  const target = view && to.startsWith('/field') ? `${to}?view=${encodeURIComponent(view)}` : to;
  return (
    <Paper component={RouterLink} to={target} variant="outlined" sx={{ p: 2, borderRadius: 1, bgcolor: '#fff', color: 'inherit', textDecoration: 'none', display: 'block' }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 900 }}>{label}</Typography>
          <Typography variant="body2" color="text.secondary">{detail}</Typography>
        </Box>
        <Typography sx={{ color: 'text.secondary', fontWeight: 900 }}>Open</Typography>
      </Stack>
    </Paper>
  );
}

function HomeView({
  tasks,
  incidents,
  attendanceRows,
  workers,
  user,
  selectedPersonnel,
}: {
  tasks: Task[];
  incidents: any[];
  attendanceRows: AttendanceRecord[];
  workers: Worker[];
  user?: { id?: string | null; email?: string | null } | null;
  selectedPersonnel?: { id?: string; email?: string } | null;
}) {
  const currentTask = currentSupervisorTaskFrom(tasks, user, selectedPersonnel);
  const [now, setNow] = useState(Date.now());
  const currentTaskRunning = currentTask ? statusKey(currentTask) === 'IN_PROGRESS' : false;
  useEffect(() => {
    if (!currentTaskRunning) return;
    setNow(Date.now());
    const interval = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [currentTask?.id, currentTaskRunning]);
  const upcoming = upcomingFrom(tasks, currentTask?.id);
  const upcomingRows = upcoming.slice(0, 6).map((task) => ({
    id: task.id,
    title: task.title,
    detail: `${task.taskCode ?? 'Task'} - Due ${formatDate(task.dueDate)}`,
    to: taskLinkFor(task),
  }));
  const pendingInspection = tasks.filter((task) => statusKey(task) === 'INSPECTION_PENDING').length;
  const lateAwaitingReview = tasks.filter((task) => statusKey(task) === 'INSPECTION_PENDING' && task.dueDate && new Date(task.dueDate) < new Date()).length;
  const remedial = tasks.filter((task) => ['INSPECTION_FAILED', 'BLOCKED'].includes(statusKey(task))).length;
  const clockedInRows = attendanceRows.filter((row) => row.scanInAt && !row.scanOutAt);
  const workerName = (row: AttendanceRecord) =>
    row.worker?.name ?? workers.find((worker) => worker.id === row.workerId)?.name ?? 'Worker';
  const currentElapsed = elapsedSecondsFor(currentTask, now);
  const currentRemaining = remainingSecondsFor(currentTask, now);
  const currentPlannedSeconds = plannedSecondsFor(currentTask);
  const currentProgress = timeProgressFor(currentTask, now);

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 1 }}>
        <MetricCard label="Pending inspection" value={pendingInspection} detail="Tasks awaiting supervisor review" />
        <MetricCard label="Late reviews" value={lateAwaitingReview} detail="Awaiting review shortly or overdue" />
        <MetricCard label="Remedial ongoing" value={remedial} detail="Incomplete or remedial task work" />
        <MetricCard label="Clocked in today" value={clockedInRows.length} detail="Worker badge scans recorded" />
        <MetricCard label="Reported incidents" value={incidents.length} detail="Field safety reports logged" />
      </Box>

      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1, bgcolor: '#fff' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 900, textTransform: 'uppercase' }}>Current task timeline</Typography>
            <Typography sx={{ fontWeight: 900 }}>
              {currentTask ? `${formatElapsed(currentRemaining)} remaining / ${formatElapsed(currentPlannedSeconds)} planned` : 'No active task timer'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {currentTask ? `${statusLabelFor(currentTask)} - elapsed ${formatElapsed(currentElapsed)}` : 'Waiting for an assigned task'}
            </Typography>
          </Box>
          <Chip size="small" label={currentTask ? statusLabelFor(currentTask) : 'No task'} color={currentTaskRunning ? 'success' : 'primary'} />
        </Stack>
        <LinearProgress variant="determinate" value={currentProgress} sx={{ height: 8, borderRadius: 1, mt: 1 }} />
      </Paper>

      <Typography sx={{ fontWeight: 900 }}>Upcoming tasks / work plan</Typography>
      <Stack spacing={1}>
        {upcomingRows.length ? upcomingRows.map((task) => (
          <MenuBar key={task.id} to={task.to} label={task.title} detail={task.detail} />
        )) : <Alert severity="info">No tasks are awaiting inspection or review.</Alert>}
      </Stack>

      <Typography sx={{ fontWeight: 900 }}>Site management</Typography>
      <Stack spacing={1}>
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1, bgcolor: '#fff' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mb: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 900, textTransform: 'uppercase' }}>Today&apos;s clock-ins</Typography>
              <Typography variant="body2" color="text.secondary">Badge scans update here for the supervisor.</Typography>
            </Box>
            <Button component={RouterLink} to="/field/attendance" variant="contained" size="small">
              Scan worker
            </Button>
          </Stack>
          <Stack spacing={0.75}>
            {attendanceRows.slice(0, 5).map((row) => (
              <Stack key={row.id} direction="row" spacing={1} alignItems="center">
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 800 }}>{workerName(row)}</Typography>
                  <Typography variant="caption" color="text.secondary">In {formatTime(row.scanInAt)}{row.scanOutAt ? ` - Out ${formatTime(row.scanOutAt)}` : ''}</Typography>
                </Box>
                <Chip size="small" color={row.scanOutAt ? 'default' : 'success'} label={row.scanOutAt ? 'out' : 'in'} />
              </Stack>
            ))}
            {!attendanceRows.length && <Alert severity="info">No worker scans recorded today.</Alert>}
          </Stack>
        </Paper>
        <MenuBar to="/field/dispatch" label="Daily dispatch" detail="Generate and send today's task packets to crews" />
        <MenuBar to="/field/timesheets" label="Timesheets" detail="Review completed work and approve submitted timesheets" />
      </Stack>

      <Button component={RouterLink} to="/field/emergency" variant="contained" size="large" sx={{ py: 1.4 }}>
        Safety & Emergency
      </Button>
    </Stack>
  );
}

function CurrentInspectionView({ task }: { task: Task }) {
  const navigate = useNavigate();
  const status = statusKey(task);
  const inspectionAction = status === 'INSPECTION_FAILED' ? 'Reinspect work' : 'Inspect work';
  const completedBy = completedByNameFor(task);
  const completedAt = task.completedAt ?? task.completionDate;
  const assignedCrew = assignedCrewNamesFor(task).join(', ') || 'Crew not recorded';
  const completedWorkers = task.completedDispatchWorkers ?? [];
  const supervisorSpecs = supervisorSpecificationItems(task);

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 1, bgcolor: '#fff', borderLeft: 5, borderLeftColor: 'success.main' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ md: 'center' }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 900, textTransform: 'uppercase' }}>
              Submitted work awaiting supervisor inspection
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 900 }}>{task.title ?? (task as any).name ?? task.taskCode ?? 'Task'}</Typography>
            <Typography variant="body2" color="text.secondary">
              {task.taskCode ?? 'Task'} - {taskLocation(task)}
            </Typography>
          </Box>
          <Chip label={status.replace(/_/g, ' ')} color={status === 'INSPECTION_FAILED' ? 'error' : 'warning'} />
        </Stack>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }, gap: 1, mt: 2 }}>
          <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 900, textTransform: 'uppercase' }}>Completed by</Typography>
            <Typography sx={{ fontWeight: 900 }}>{completedBy}</Typography>
            <Typography variant="body2" color="text.secondary">{completedAt ? formatDateTime(completedAt) : 'Completion time not recorded'}</Typography>
            {completedWorkers.length > 0 && (
              <Stack spacing={0.5} sx={{ mt: 1 }}>
                {completedWorkers.map((worker, index) => (
                  <Stack key={`${worker.id ?? worker.userId ?? worker.name ?? index}`} direction="row" spacing={1} justifyContent="space-between">
                    <Typography variant="caption" sx={{ fontWeight: 800 }}>{worker.name || worker.userEmail || 'Worker'}</Typography>
                    <Typography variant="caption" color="text.secondary">{worker.completedAt ? formatDateTime(worker.completedAt) : 'Submitted'}</Typography>
                  </Stack>
                ))}
              </Stack>
            )}
          </Paper>
          <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 900, textTransform: 'uppercase' }}>Assigned to</Typography>
            <Typography sx={{ fontWeight: 900 }}>{assignedCrew}</Typography>
            <Typography variant="body2" color="text.secondary">Crew responsible for this work</Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 900, textTransform: 'uppercase' }}>Worker reported</Typography>
            <Typography sx={{ fontWeight: 900 }}>{Number(task.actualHours ?? 0).toFixed(1)} hrs</Typography>
            <Typography variant="body2" color="text.secondary">{Number(task.estimatedHours ?? 0).toFixed(1)} hrs expected</Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 900, textTransform: 'uppercase' }}>Inspection focus</Typography>
            <Typography sx={{ fontWeight: 900 }}>{status === 'INSPECTION_FAILED' ? 'Rework verification' : 'First review'}</Typography>
            <Typography variant="body2" color="text.secondary">Pass, conditional, or fail decision</Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 900, textTransform: 'uppercase' }}>Evidence</Typography>
            <Typography sx={{ fontWeight: 900 }}>Photos / task sheet</Typography>
            <Typography variant="body2" color="text.secondary">Review before verdict</Typography>
          </Paper>
        </Box>

        <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1, mt: 1.5 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 900, textTransform: 'uppercase' }}>Specifications</Typography>
          {supervisorSpecs.length ? (
            <Stack component="ul" spacing={0.75} sx={{ m: 0, mt: 0.75, pl: 2.5 }}>
              {supervisorSpecs.map((spec, index) => (
                <Typography component="li" variant="body2" key={`${spec.text}-${index}`}>
                  {spec.text}
                </Typography>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              No supervisor specifications are linked to this task.
            </Typography>
          )}
        </Paper>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 2 }}>
          <Button variant="contained" color="success" onClick={() => navigate(`/field/inspections/${task.id}`)}>
            {inspectionAction}
          </Button>
          <Button variant="outlined" onClick={() => navigate(`/field/tasks/${task.id}`)}>Review task sheet</Button>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 1, bgcolor: '#fff' }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 900, textTransform: 'uppercase' }}>
          Location
        </Typography>
        <Typography sx={{ fontWeight: 900, mb: 1 }}>{taskLocation(task)}</Typography>
        <SitePlanMap task={task} compact />
      </Paper>
    </Stack>
  );
}

function CurrentSupervisorTaskView({
  task,
  workers,
  workerUsers,
  attendanceRows,
}: {
  task: Task;
  workers: Worker[];
  workerUsers: UserListItem[];
  attendanceRows: AttendanceRecord[];
}) {
  const status = statusKey(task);
  const [now, setNow] = useState(Date.now());
  const [sending, setSending] = useState(false);
  const [sendNotice, setSendNotice] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [workerDialogOpen, setWorkerDialogOpen] = useState(false);
  const [showAlternatives, setShowAlternatives] = useState(false);
  const [selectedWorkerIds, setSelectedWorkerIds] = useState<Set<string>>(new Set());
  const [dispatchedWorkers, setDispatchedWorkers] = useState<ProgressWorker[]>([]);
  const assignedCrewId = assignedCrewIdFor(task);
  const crewNames = assignedCrewNamesFor(task);
  const assignedCrewWorkers = useMemo(
    () => workers.filter((worker) => worker.isActive !== false && worker.teamId === assignedCrewId),
    [workers, assignedCrewId]
  );
  const alternativeWorkers = useMemo(
    () => {
      const presentIds = new Set(attendanceRows.map((row) => row.workerId));
      return workers
        .filter((worker) => worker.isActive !== false && worker.teamId !== assignedCrewId && isExecutionWorker(worker))
        .sort((a, b) => Number(presentIds.has(b.id)) - Number(presentIds.has(a.id)) || a.name.localeCompare(b.name));
    },
    [workers, assignedCrewId, attendanceRows]
  );
  const selectedWorkers = useMemo(
    () => workers.filter((worker) => selectedWorkerIds.has(worker.id)),
    [workers, selectedWorkerIds]
  );
  const taskDispatchWorkers = useMemo<ProgressWorker[]>(
    () => (task.dispatchWorkers ?? []).map((worker) => ({
      id: worker.id ?? worker.userId ?? worker.name ?? 'worker',
      name: worker.name || worker.userEmail || 'Worker',
      badgeCode: worker.badgeCode ?? undefined,
      teamId: worker.teamId ?? undefined,
      trade: worker.trade ?? undefined,
      isActive: true,
      lifecycleStatus: worker.lifecycleStatus ?? 'ready',
      startedAt: worker.startedAt ?? null,
      completedAt: worker.completedAt ?? null,
      actualSeconds: worker.actualSeconds ?? 0,
    } as ProgressWorker)),
    [task.dispatchWorkers]
  );
  const progressWorkers = dispatchedWorkers.length
    ? dispatchedWorkers
    : taskDispatchWorkers.length
      ? taskDispatchWorkers
      : selectedWorkers.length
        ? selectedWorkers
        : assignedCrewWorkers;
  const workerUserByName = useMemo(() => {
    const map = new Map<string, UserListItem>();
    workerUsers.forEach((workerUser) => {
      map.set(normalizedPersonName(workerUser.firstName, workerUser.lastName), workerUser);
    });
    return map;
  }, [workerUsers]);
  const accountEmailFor = (worker: Worker) => workerUserByName.get(normalizedWorkerName(worker))?.email;
  const presentWorkerIds = useMemo(() => new Set(attendanceRows.map((row) => row.workerId)), [attendanceRows]);
  const taskRunning = status === 'IN_PROGRESS';
  const elapsedSeconds = elapsedSecondsFor(task, now);
  const remainingSeconds = remainingSecondsFor(task, now);
  const plannedSeconds = plannedSecondsFor(task);
  const timeProgress = timeProgressFor(task, now);
  const supervisorSpecs = supervisorSpecificationItems(task);
  useEffect(() => {
    if (!taskRunning) return;
    setNow(Date.now());
    const interval = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [task.id, taskRunning]);
  useEffect(() => {
    let cancelled = false;
    setDispatchedWorkers([]);
    if (!task.projectId) return undefined;

    const workersById = new Map(workers.map((worker) => [worker.id, worker]));
    dailyDispatchService.listDispatches({
      projectId: task.projectId,
      dispatchDate: todayIso(),
    })
      .then(async (dispatches) => {
        const sentDispatches = dispatches.filter((dispatch) => ['sent', 'partially_sent'].includes(dispatch.status));
        const packets = await Promise.all(sentDispatches.map((dispatch) => dailyDispatchService.getDispatch(dispatch.id)));
        if (cancelled) return;
        const selectedById = new Map<string, ProgressWorker>();
        packets.forEach((packet) => {
          packet.items
            .filter((item) => item.taskId === task.id)
            .forEach((item) => {
              const selected = Array.isArray(item.metadata?.selectedWorkers)
                ? item.metadata.selectedWorkers as DispatchSelectedWorker[]
                : [];
              selected.forEach((worker) => {
                const id = worker.id ?? null;
                if (!id || selectedById.has(id)) return;
                selectedById.set(id, {
                  ...(workersById.get(id) ?? {
                  id,
                  name: worker.name || 'Worker',
                  badgeCode: worker.badgeCode ?? undefined,
                  teamId: worker.teamId ?? undefined,
                  trade: worker.trade ?? undefined,
                  isActive: true,
                  } as Worker),
                  lifecycleStatus: worker.lifecycleStatus ?? 'ready',
                  startedAt: worker.startedAt ?? null,
                  completedAt: worker.completedAt ?? null,
                  actualSeconds: worker.actualSeconds ?? 0,
                });
              });
            });
        });
        setDispatchedWorkers([...selectedById.values()]);
      })
      .catch(() => {
        if (!cancelled) setDispatchedWorkers([]);
      });

    return () => {
      cancelled = true;
    };
  }, [task.id, task.projectId, workers]);
  const openWorkerDispatchDialog = () => {
    const currentWorkerIds = dispatchedWorkers.length
      ? dispatchedWorkers.map((worker) => worker.id)
      : assignedCrewWorkers.map((worker) => worker.id);
    setSelectedWorkerIds(new Set(currentWorkerIds));
    setShowAlternatives(false);
    setWorkerDialogOpen(true);
  };
  const toggleWorker = (workerId: string) => {
    setSelectedWorkerIds((current) => {
      const next = new Set(current);
      if (next.has(workerId)) next.delete(workerId);
      else next.add(workerId);
      return next;
    });
  };
  const selectAssignedCrewWorkers = () => {
    setSelectedWorkerIds(new Set(assignedCrewWorkers.map((worker) => worker.id)));
  };
  const addAlternativeWorkers = () => {
    setSelectedWorkerIds((current) => {
      const next = new Set(current);
      alternativeWorkers.forEach((worker) => next.add(worker.id));
      return next;
    });
  };
  const sendToWorker = async () => {
    setSending(true);
    setSendNotice(null);
    setSendError(null);
    try {
      const latestTask = await tasksService.getTaskById(task.id);
      const latestStatus = statusKey(latestTask);
      const latestAssignedCrewId = assignedCrewIdFor(latestTask);
      const latestCrewNames = assignedCrewNamesFor(latestTask);

      if (!latestTask.projectId) {
        setSendError('This task is missing a project, so it cannot be sent to workers yet.');
        return;
      }
      if (!latestAssignedCrewId) {
        setSendError('A crew must be assigned before this task can be sent to workers.');
        return;
      }
      if (!latestTask.materialsReady) {
        setSendError('Materials are still not marked ready on the server. Mark Ready Materials from the project manager task screen, then send it again.');
        return;
      }
      if (latestStatus !== 'READY') {
        await tasksService.updateTaskStatus(latestTask.id, 'READY');
      }

      const dispatchDate = toLocalIsoDate(new Date());
      const packet = await dailyDispatchService.generateDraft({
        projectId: latestTask.projectId,
        dispatchDate,
        teamId: latestAssignedCrewId,
        taskIds: [latestTask.id],
        workerIds: [...selectedWorkerIds],
        title: `${latestTask.taskCode ?? 'Task'} - ${latestCrewNames[0] ?? 'Crew'} work packet`,
        notes: 'Sent from Supervisor Current Task.',
        deliveryChannel: 'in_app',
      });
      await dailyDispatchService.sendDispatch(packet.dispatch.id, 'in_app');
      setDispatchedWorkers(workers.filter((worker) => selectedWorkerIds.has(worker.id)));
      setWorkerDialogOpen(false);
      setSendNotice(
        selectedWorkerIds.size
          ? `Task sent to ${selectedWorkerIds.size} selected worker account${selectedWorkerIds.size === 1 ? '' : 's'}.`
          : 'Task sent to the assigned worker/crew.'
      );
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.response?.data?.error || '';
      if (/task_not_ready|materials_not_ready/i.test(message)) {
        setSendError('The task could not be prepared for worker dispatch. Open the task and confirm it is not still in Draft or blocked by a hard project rule.');
      } else if (/already dispatched/i.test(message)) {
        setSendNotice('This task has already been sent to the assigned worker/crew for today.');
      } else {
        setSendError(message || 'Task could not be sent to workers.');
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <Stack spacing={2}>
      {sendNotice && <Alert severity="success" onClose={() => setSendNotice(null)}>{sendNotice}</Alert>}
      {sendError && <Alert severity="error" onClose={() => setSendError(null)}>{sendError}</Alert>}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 1, bgcolor: '#fff', borderLeft: 5, borderLeftColor: 'primary.main' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ md: 'center' }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 900, textTransform: 'uppercase' }}>
              Current task sent by project manager
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 900 }}>{task.title}</Typography>
            <Typography variant="body2" color="text.secondary">
              {task.taskCode ?? 'Task'} - {taskLocation(task)}
            </Typography>
          </Box>
          <Chip label={statusLabelFor(task)} color={status === 'BLOCKED' ? 'error' : taskRunning ? 'success' : 'primary'} />
        </Stack>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1, mt: 2 }}>
          <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 900, textTransform: 'uppercase' }}>Assigned crew</Typography>
            <Typography sx={{ fontWeight: 900 }}>{crewNames.length ? crewNames.join(', ') : 'Crew not assigned'}</Typography>
            <Typography variant="body2" color="text.secondary">Supervisor can open the task sheet to dispatch work.</Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 900, textTransform: 'uppercase' }}>Issued</Typography>
            <Typography sx={{ fontWeight: 900 }}>{formatDate(task.pmIssuedAt)}</Typography>
            <Typography variant="body2" color="text.secondary">This is now the active supervisor task.</Typography>
          </Paper>
        </Box>

        <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1, mt: 1.5 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 900, textTransform: 'uppercase' }}>Specifications</Typography>
          {supervisorSpecs.length ? (
            <Stack component="ul" spacing={0.75} sx={{ m: 0, mt: 0.75, pl: 2.5 }}>
              {supervisorSpecs.map((spec, index) => (
                <Typography component="li" variant="body2" key={`${spec.text}-${index}`}>
                  {spec.text}
                </Typography>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              No supervisor specifications are linked to this task.
            </Typography>
          )}
        </Paper>

        <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1, mt: 1.5 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 900, textTransform: 'uppercase' }}>Worker progress timer</Typography>
              <Typography sx={{ fontWeight: 900 }}>
                {taskRunning ? `${formatElapsed(remainingSeconds)} remaining` : `${formatElapsed(elapsedSeconds)} elapsed`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatElapsed(elapsedSeconds)} elapsed / {formatElapsed(plannedSeconds)} planned
              </Typography>
            </Box>
            <Chip size="small" label={`${timeProgress}%`} color={taskRunning ? 'success' : 'default'} />
          </Stack>
          <LinearProgress variant="determinate" value={timeProgress} sx={{ height: 8, borderRadius: 1, mt: 1 }} />
        </Paper>

        {progressWorkers.length > 0 && (
          <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1, mt: 1.5 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'center' }} sx={{ mb: 1 }}>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 900, textTransform: 'uppercase' }}>Worker progress</Typography>
                <Typography variant="body2" color="text.secondary">
                  {dispatchedWorkers.length || selectedWorkers.length ? 'Selected workers on this task' : 'Assigned crew workers'}
                </Typography>
              </Box>
              <Chip size="small" label={`${progressWorkers.length} worker${progressWorkers.length === 1 ? '' : 's'}`} />
            </Stack>
            <Stack spacing={0.75}>
              {progressWorkers.map((worker) => {
                const workerStatus = String((worker as ProgressWorker).lifecycleStatus ?? (dispatchedWorkers.length ? 'ready' : status)).toUpperCase();
                const workerPercent = workerProgressPercent(worker as ProgressWorker, task, now);
                const elapsed = workerElapsedSeconds(worker as ProgressWorker, now);
                return (
                  <Box key={worker.id}>
                    <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" fontWeight={900}>{worker.name}</Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip size="small" label={lifecycleLabels[workerStatus] ?? workerStatus.replace(/_/g, ' ')} />
                        <Typography variant="caption" color="text.secondary">{formatElapsed(elapsed)}</Typography>
                        <Typography variant="caption" color="text.secondary">{workerPercent}%</Typography>
                      </Stack>
                    </Stack>
                    <LinearProgress variant="determinate" value={workerPercent} sx={{ height: 5, borderRadius: 1 }} />
                  </Box>
                );
              })}
            </Stack>
          </Paper>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 2 }}>
          <Button component={RouterLink} to={`/field/tasks/${task.id}`} variant="contained">
            Open Task
          </Button>
          <Button variant="contained" color="success" onClick={openWorkerDispatchDialog} disabled={sending || !assignedCrewId}>
            Send to worker
          </Button>
          <Button component={RouterLink} to="/field/supervisor/upcoming" variant="outlined">
            Upcoming Work Plan
          </Button>
        </Stack>
        {crewNames.length === 0 && (
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            A crew must be assigned by the Project Manager before this task can be sent to workers.
          </Alert>
        )}
      </Paper>

      <Dialog open={workerDialogOpen} onClose={() => !sending && setWorkerDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send to Worker</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}>
              <Typography variant="overline" color="text.secondary" fontWeight={900}>Assigned Crew</Typography>
              <Typography variant="body1" fontWeight={900}>{crewNames.length ? crewNames.join(', ') : 'Crew not assigned'}</Typography>
              <Typography variant="body2" color="text.secondary">
                These workers belong to the crew selected by the Project Manager.
              </Typography>
            </Paper>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button variant="outlined" onClick={selectAssignedCrewWorkers} disabled={assignedCrewWorkers.length === 0}>
                Select assigned crew ({assignedCrewWorkers.length})
              </Button>
              <Button variant={showAlternatives ? 'contained' : 'outlined'} onClick={() => setShowAlternatives((current) => !current)}>
                Alternative workers ({alternativeWorkers.length})
              </Button>
              {showAlternatives && (
                <Button variant="contained" onClick={addAlternativeWorkers} disabled={alternativeWorkers.length === 0}>
                  Add all alternatives
                </Button>
              )}
            </Stack>
            <Alert severity="info">
              {selectedWorkerIds.size} worker account{selectedWorkerIds.size === 1 ? '' : 's'} selected for this task.
            </Alert>

            <Stack spacing={1}>
              <Typography variant="subtitle2" fontWeight={900}>Crew worker accounts</Typography>
              {assignedCrewWorkers.length ? assignedCrewWorkers.map((worker) => (
                <Paper
                  key={worker.id}
                  variant="outlined"
                  onClick={() => toggleWorker(worker.id)}
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    cursor: 'pointer',
                    borderColor: selectedWorkerIds.has(worker.id) ? 'primary.main' : 'divider',
                    bgcolor: selectedWorkerIds.has(worker.id) ? 'action.selected' : '#fff',
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Checkbox
                      checked={selectedWorkerIds.has(worker.id)}
                      onClick={(event) => event.stopPropagation()}
                      onChange={() => toggleWorker(worker.id)}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography fontWeight={900}>{worker.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {accountEmailFor(worker) || worker.trade || worker.badgeCode || 'Crew worker'}
                      </Typography>
                    </Box>
                    {presentWorkerIds.has(worker.id) && <Chip size="small" color="success" label="On site" />}
                    <Chip size="small" color="primary" label="Assigned crew" />
                  </Stack>
                </Paper>
              )) : (
                <Alert severity="warning">No active worker accounts are linked to this crew yet.</Alert>
              )}
            </Stack>

            {showAlternatives && (
              <Stack spacing={1}>
                <Divider />
                <Typography variant="subtitle2" fontWeight={900}>Alternative worker accounts</Typography>
                {alternativeWorkers.map((worker) => (
                  <Paper
                    key={worker.id}
                    variant="outlined"
                    onClick={() => toggleWorker(worker.id)}
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      cursor: 'pointer',
                      borderColor: selectedWorkerIds.has(worker.id) ? 'primary.main' : 'divider',
                      bgcolor: selectedWorkerIds.has(worker.id) ? 'action.selected' : '#fff',
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Checkbox
                        checked={selectedWorkerIds.has(worker.id)}
                        onClick={(event) => event.stopPropagation()}
                        onChange={() => toggleWorker(worker.id)}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography fontWeight={900}>{worker.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {accountEmailFor(worker) || worker.trade || worker.badgeCode || 'Worker account'}
                        </Typography>
                      </Box>
                      {presentWorkerIds.has(worker.id) && <Chip size="small" color="success" label="On site" />}
                      <Button
                        size="small"
                        variant={selectedWorkerIds.has(worker.id) ? 'contained' : 'outlined'}
                        onClick={(event) => {
                          event.stopPropagation();
                          toggleWorker(worker.id);
                        }}
                      >
                        {selectedWorkerIds.has(worker.id) ? 'Selected' : 'Add'}
                      </Button>
                    </Stack>
                  </Paper>
                ))}
                {alternativeWorkers.length === 0 && <Alert severity="info">No alternative workers are available.</Alert>}
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWorkerDialogOpen(false)} disabled={sending}>Cancel</Button>
          <Button variant="contained" color="success" onClick={() => void sendToWorker()} disabled={sending || selectedWorkerIds.size === 0}>
            {sending ? 'Sending...' : `Send to ${selectedWorkerIds.size} worker${selectedWorkerIds.size === 1 ? '' : 's'}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

function UpcomingView({ tasks, currentTaskId }: { tasks: Task[]; currentTaskId?: string }) {
  const navigate = useNavigate();
  const [range, setRange] = useState<'week' | 'month'>('week');
  const upcoming = upcomingFrom(tasks, currentTaskId);
  const days = range === 'week' ? ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] : Array.from({ length: 30 }, (_, index) => String(index + 1));

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="h6" sx={{ fontWeight: 900, flex: 1 }}>Upcoming tasks / workflow plan</Typography>
        <ToggleButtonGroup exclusive size="small" value={range} onChange={(_, value) => value && setRange(value)}>
          <ToggleButton value="week">Week</ToggleButton>
          <ToggleButton value="month">Month</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <Paper variant="outlined" sx={{ p: 1, borderRadius: 1, bgcolor: '#fff' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: range === 'week' ? 'repeat(7, 1fr)' : 'repeat(6, 1fr)', gap: 0.75 }}>
          {days.map((day, index) => (
            <Box key={day} sx={{ minHeight: range === 'week' ? 62 : 42, border: '1px solid', borderColor: 'divider', borderRadius: 0.75, p: 0.75 }}>
              <Typography variant="caption" sx={{ fontWeight: 900 }}>{day}</Typography>
              {index < upcoming.length && <Box sx={{ mt: 0.5, height: 6, bgcolor: 'primary.main', borderRadius: 1 }} />}
            </Box>
          ))}
        </Box>
      </Paper>

      <Typography variant="h6" sx={{ fontWeight: 900 }}>Task schedule</Typography>
      <Stack spacing={1.25}>
        {upcoming.length ? upcoming.map((task) => {
          const awaitingInspection = taskReadyForSupervisorInspection(task);
          const completedAt = task.completedAt ?? task.completionDate;
          return (
          <Paper key={task.id} variant="outlined" onClick={() => navigate(taskLinkFor(task))} sx={{ p: 2, borderRadius: 1, bgcolor: '#fff', cursor: 'pointer' }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 900 }}>{task.title}</Typography>
                <Typography variant="body2" color="text.secondary">{task.taskCode ?? 'Task'} - {taskLocation(task)} - Due {formatDate(task.dueDate)}</Typography>
                {awaitingInspection && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35, fontWeight: 800 }}>
                    Completed by {completedByNameFor(task)}{completedAt ? ` - ${formatDateTime(completedAt)}` : ''}
                  </Typography>
                )}
              </Box>
              <ExpandMoreIcon />
            </Stack>
          </Paper>
          );
        }) : <Alert severity="info">No upcoming inspection tasks are assigned.</Alert>}
      </Stack>
    </Stack>
  );
}

function QcChecklistContent({ task, reload }: { task: Task; reload: () => void }) {
  const navigate = useNavigate();
  const [checkedRows, setCheckedRows] = useState<Record<number, boolean>>({});
  const [photoCounts, setPhotoCounts] = useState<Record<number, number>>({});
  const [linkedInspection, setLinkedInspection] = useState<InspectionRow | null>(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const specs = supervisorSpecificationItems(task);
  const checklistSpecs = specs.length ? specs : [
    { text: 'Workmanship matches approved specification.', done: false },
    { text: 'Finished work is ready for next trade.', done: false },
  ];

  useEffect(() => {
    let cancelled = false;
    setLinkedInspection(null);
    if (!task.projectId) return undefined;
    inspectionsService.list({ projectId: task.projectId, taskId: task.id })
      .then((rows) => {
        if (cancelled) return;
        setLinkedInspection(rows.find((row) => !['passed', 'failed', 'cancelled'].includes(row.status)) ?? rows[0] ?? null);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [task.id, task.projectId]);

  const queueRowPhoto = async (index: number, specText: string, file?: File | null) => {
    if (!file) return;
    setPhotoBusy(true);
    setActionError(null);
    try {
      const payload = await photoEvidencePayload(file);
      await enqueueOfflineFieldEvidence(task, { ...payload, notes: `QC check photo: ${specText}` });
      const synced = navigator.onLine ? await fieldSyncService.flushPending().catch(() => null) : null;
      setPhotoCounts((current) => ({ ...current, [index]: (current[index] ?? 0) + 1 }));
      setNotice(synced?.synced ? 'Photo evidence uploaded.' : 'Photo evidence queued for sync.');
    } catch {
      setActionError('Photo could not be queued.');
    } finally {
      setPhotoBusy(false);
    }
  };

  const approve = async () => {
    setSaving(true);
    setActionError(null);
    try {
      const result = await submitInspectionVerdict(task, linkedInspection, 'passed');
      setNotice(result.queuedOffline ? 'Approval queued for sync.' : 'Inspection marked approved.');
      reload();
    } catch (err: any) {
      setActionError(err?.response?.data?.message || err?.response?.data?.error || 'Verdict could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={1.5}>
      {actionError && <Alert severity="error" onClose={() => setActionError(null)}>{actionError}</Alert>}
      <Paper variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden', bgcolor: '#fff' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Specification</TableCell>
              <TableCell>QC checks</TableCell>
              <TableCell>Acceptance criteria</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell>Photo evidence</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {checklistSpecs.map((spec, index) => (
              <TableRow key={`${spec.text}-${index}`}>
                <TableCell>{spec.text}</TableCell>
                <TableCell>
                  <Checkbox
                    size="small"
                    sx={{ p: 0 }}
                    checked={Boolean(checkedRows[index])}
                    onChange={(event) => setCheckedRows((current) => ({ ...current, [index]: event.target.checked }))}
                    inputProps={{ 'aria-label': `QC check: ${spec.text}` }}
                  />
                </TableCell>
                <TableCell>Meets drawing/spec tolerance</TableCell>
                <TableCell>Supervisor observation</TableCell>
                <TableCell>
                  <Button
                    variant="outlined"
                    component="label"
                    size="small"
                    color={photoCounts[index] ? 'success' : 'primary'}
                    disabled={photoBusy}
                    startIcon={<PhotoIcon />}
                  >
                    {photoCounts[index] ? `Photo (${photoCounts[index]})` : 'Photo'}
                    <input
                      hidden
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={(event) => {
                        const file = event.target.files?.[0] ?? null;
                        void queueRowPhoto(index, spec.text, file);
                        event.target.value = '';
                      }}
                    />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 1, bgcolor: '#fff' }}>
        <Typography sx={{ fontWeight: 900, mb: 1 }}>Overall inspection verdict</Typography>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          <Button variant="contained" color="success" disabled={saving} onClick={() => void approve()}>Approved</Button>
          <Button variant="contained" color="warning" disabled={saving} onClick={() => navigate(`/field/inspections/${task.id}`)}>Incomplete</Button>
          <Button variant="contained" color="error" disabled={saving} onClick={() => navigate(`/field/inspections/${task.id}`)}>Failed</Button>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Incomplete and Failed open the inspection detail to record reasons and defects.
        </Typography>
      </Paper>

      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={4000}
        onClose={() => setNotice(null)}
        message={notice ?? ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Stack>
  );
}

function QcChecklistView({ task, reload }: { task: Task; reload: () => void }) {
  return <QcChecklistContent task={task} reload={reload} />;
}

function EmergencyView({
  tasks,
  contacts,
  incidents,
  toolboxTalks,
  teams,
  workers,
}: {
  tasks: Task[];
  contacts: any[];
  incidents: any[];
  toolboxTalks: any[];
  teams: TeamListItem[];
  workers: Worker[];
}) {
  const [expanded, setExpanded] = useState<string | false>('toolbox');
  const activeTeams = teams.filter((team) => team.isActive !== false);
  const fallbackGear = ['Hard hat', 'Hi-vis vest', 'Safety boots', 'Gloves', 'Eye protection'];
  const crewRows = activeTeams.length
    ? activeTeams
    : [{ id: 'site-crew', name: 'Site crew', teamType: 'construction', members: [] } as TeamListItem];
  const workersForTeam = (teamId: string) => workers.filter((worker) => worker.teamId === teamId && worker.isActive !== false);
  const tasksForTeam = (teamId: string) => tasks.filter((task) => taskTeamIds(task).includes(teamId));
  const incidentTeamId = (incident: any) => incident.teamId ?? incident.assignedToTeamId ?? incident.team_id ?? null;
  const incidentTitle = (incident: any) =>
    incident.title ?? incident.description ?? incident.incidentType ?? incident.type ?? 'Incident report';
  const contactLabel = (contact: any) => {
    const name = contact.name ?? contact.contactName ?? 'Emergency contact';
    const role = contact.contactRole ?? contact.role;
    const phone = contact.phone ?? contact.phoneNumber ?? contact.mobile ?? '-';
    return [name, role, phone].filter(Boolean).join(' - ');
  };
  const contactPhone = (contact: any) => String(contact.phone ?? contact.phoneNumber ?? contact.mobile ?? '').trim();
  const contactHref = (contact: any) => {
    const phone = contactPhone(contact).replace(/[^\d+]/g, '');
    return phone ? `tel:${phone}` : undefined;
  };
  const gearForTeam = (teamTasks: Task[]) => {
    const taskGear = Array.from(new Set(teamTasks.flatMap((task) => {
      const resources = Array.isArray(task.resourcesRequired) ? task.resourcesRequired : [];
      return resources
        .map((resource: any) => resource.name ?? resource.resourceName ?? resource.description)
        .filter((value: string | undefined) => value && /hat|helmet|vest|boot|glove|goggle|eye|mask|harness|ppe/i.test(value));
    })));
    return taskGear.length ? taskGear : fallbackGear;
  };

  const sections = [
    ['toolbox', 'Toolbox talks - information', toolboxTalks.length ? toolboxTalks.map((talk) => talk.topic ?? talk.title ?? talk.subject ?? 'Toolbox talk') : ['Daily toolbox talk and work method briefing.']],
    ['contacts', 'Emergency contact information', contacts.length ? contacts.map(contactLabel) : ['Ambulance / Fire - 110', 'Site manager - site radio']],
    ['meetings', 'Site meetings report', tasks.length ? tasks.slice(0, 4).map((task) => `${task.taskCode ?? 'Task'} - coordination notes`) : ['No site meeting records loaded.']],
  ];

  return (
    <Stack spacing={2}>
      <Accordion expanded={expanded === 'gear'} onChange={(_, open) => setExpanded(open ? 'gear' : false)} disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 900 }}>Safety gears assigned</Typography>
            <Typography variant="caption" color="text.secondary">{crewRows.length} crew folder{crewRows.length === 1 ? '' : 's'}</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={1}>
            {crewRows.map((team) => {
              const teamWorkers = workersForTeam(team.id);
              const gear = gearForTeam(tasksForTeam(team.id));
              const displayWorkers = teamWorkers.length
                ? teamWorkers
                : [{ id: `${team.id}-crew`, name: `${team.name} crew member`, badgeCode: null, teamId: team.id, trade: team.teamType, isActive: true } as Worker];
              return (
                <Accordion key={team.id} disableGutters variant="outlined" sx={{ borderRadius: 1, '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography fontWeight={900}>{team.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{displayWorkers.length} crew member{displayWorkers.length === 1 ? '' : 's'}</Typography>
                      </Box>
                      <Chip size="small" label={team.teamType} />
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <Stack spacing={1}>
                      {displayWorkers.map((worker) => (
                        <Paper key={worker.id} variant="outlined" sx={{ p: 1.25, borderRadius: 1 }}>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
                            <Box>
                              <Typography fontWeight={900}>{worker.name}</Typography>
                              <Typography variant="caption" color="text.secondary">{worker.trade || worker.badgeCode || 'Crew member'}</Typography>
                            </Box>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              {gear.map((item) => <Chip key={`${worker.id}-${item}`} size="small" variant="outlined" label={item} />)}
                            </Stack>
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion expanded={expanded === 'incidents'} onChange={(_, open) => setExpanded(open ? 'incidents' : false)} disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 900 }}>Field and incident report</Typography>
            <Typography variant="caption" color="text.secondary">Grouped by crew</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={1}>
            {crewRows.map((team) => {
              const teamIncidents = incidents.filter((incident) => incidentTeamId(incident) === team.id);
              const teamTasks = tasksForTeam(team.id).slice(0, 4);
              const incidentRows = teamIncidents.length
                ? teamIncidents.map((incident) => incidentTitle(incident))
                : ['No incident reports loaded for this crew.'];
              return (
                <Accordion key={`incident-${team.id}`} disableGutters variant="outlined" sx={{ borderRadius: 1, '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography fontWeight={900}>{team.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{teamIncidents.length} incident report{teamIncidents.length === 1 ? '' : 's'}</Typography>
                      </Box>
                      <Chip size="small" label={`${teamTasks.length} field notes`} />
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <Stack spacing={1}>
                      <Typography variant="caption" color="text.secondary" fontWeight={900}>Incident reports</Typography>
                      {incidentRows.map((row, index) => (
                        <Paper key={`${team.id}-incident-${index}`} variant="outlined" sx={{ p: 1.25, borderRadius: 1 }}>
                          <Typography variant="body2">{row}</Typography>
                        </Paper>
                      ))}
                      <Typography variant="caption" color="text.secondary" fontWeight={900}>Field notes</Typography>
                      {teamTasks.length ? teamTasks.map((task) => (
                        <Paper key={`${team.id}-task-${task.id}`} variant="outlined" sx={{ p: 1.25, borderRadius: 1 }}>
                          <Typography fontWeight={800}>{task.taskCode ?? 'Task'} - {task.title}</Typography>
                          <Typography variant="body2" color="text.secondary">{taskLocation(task)} - {statusKey(task).replace(/_/g, ' ')}</Typography>
                        </Paper>
                      )) : (
                        <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1 }}>
                          <Typography variant="body2">No field task notes loaded for this crew.</Typography>
                        </Paper>
                      )}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Stack>
        </AccordionDetails>
      </Accordion>

      {sections.map(([id, label, rows]) => (
        <Accordion key={String(id)} expanded={expanded === id} onChange={(_, open) => setExpanded(open ? String(id) : false)} disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 900 }}>{String(label)}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1}>
              {String(id) === 'contacts' && contacts.length ? contacts.map((contact, index) => (
                <Button
                  key={`contact-${contact.id ?? index}`}
                  component="a"
                  href={contactHref(contact)}
                  variant="outlined"
                  color="error"
                  disabled={!contactPhone(contact)}
                  sx={{ justifyContent: 'flex-start', textAlign: 'left', py: 1.25, borderRadius: 1 }}
                >
                  {contactLabel(contact)}
                </Button>
              )) : (rows as string[]).map((row, index) => (
                <Paper key={`${String(id)}-${index}`} variant="outlined" sx={{ p: 1.25, borderRadius: 1 }}>
                  <Typography>{row}</Typography>
                </Paper>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}
    </Stack>
  );
}

export default function SupervisorFieldModePage({ view }: SupervisorFieldModePageProps) {
  const user = useSelector((state: RootState) => state.auth.user);
  const { activeProjectId, projects, loading: projectsLoading } = useSelector((state: RootState) => state.projects);
  const effectiveProjectId = activeProjectId ?? projects[0]?.id ?? null;
  const [searchParams, setSearchParams] = useSearchParams();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [teams, setTeams] = useState<TeamListItem[]>([]);
  const [supervisorUsers, setSupervisorUsers] = useState<UserListItem[]>([]);
  const [workerUsers, setWorkerUsers] = useState<UserListItem[]>([]);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [attendanceRows, setAttendanceRows] = useState<AttendanceRecord[]>([]);
  const [contacts, setContacts] = useState<any[]>([]);
  const [incidents, setIncidents] = useState<any[]>([]);
  const [toolboxTalks, setToolboxTalks] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const projectName = projects.find((project) => project.id === effectiveProjectId)?.name ?? 'Active project';
  const normalizedView = view === 'inspections' ? 'home' : view;
  const allowPersonnelSelection = ['admin', 'field_admin'].includes(user?.role ?? '');
  const supervisorPersonnel = useMemo(() => mergeSupervisorPersonnel(supervisorUsers), [supervisorUsers]);
  const selectedPersonnel = allowPersonnelSelection
    ? fieldPersonnelById(supervisorPersonnel, searchParams.get(ACCOUNTS_PERSONNEL_QUERY_KEY))
    : null;
  const currentTask = currentSupervisorTaskFrom(tasks, user, selectedPersonnel);
  const currentInspectionTask = currentInspectionFrom(tasks, user, selectedPersonnel);

  const load = useCallback(async () => {
    if (!effectiveProjectId) return;
    setLoading(true);
    setError(null);
    try {
      const [taskRows, teamRows, supervisorUserRows, workerUserRows, workerRows, attendanceResult, contactRows, incidentRows, toolboxRows] = await Promise.allSettled([
        tasksService.getTasks({ supervisorHandoff: true, limit: 100 }),
        teamService.getTeams(),
        userService.searchUsers({ role: 'supervisor', limit: 250 }),
        userService.searchUsers({ role: 'construction', limit: 500 }),
        timesheetService.listWorkers({ active: true, includeAllTeams: true }),
        attendanceService.list({ projectId: effectiveProjectId, attendanceDate: todayIso() }),
        readFirstRegisterService.list('/emergency-contacts', { projectId: effectiveProjectId }),
        readFirstRegisterService.list('/incidents', { projectId: effectiveProjectId }),
        readFirstRegisterService.list('/toolbox-talks', { projectId: effectiveProjectId }),
      ]);
      setTasks(taskRows.status === 'fulfilled' ? taskRows.value.tasks : []);
      setTeams(teamRows.status === 'fulfilled' ? teamRows.value.filter((team) => team.isActive !== false) : []);
      setSupervisorUsers(supervisorUserRows.status === 'fulfilled' ? supervisorUserRows.value : []);
      setWorkerUsers(workerUserRows.status === 'fulfilled' ? workerUserRows.value : []);
      const visibleWorkers = workerRows.status === 'fulfilled' ? workerRows.value.filter((worker) => worker.isActive !== false) : [];
      const visibleWorkerIds = new Set(visibleWorkers.map((worker) => worker.id));
      setWorkers(visibleWorkers);
      setAttendanceRows(attendanceResult.status === 'fulfilled'
        ? attendanceResult.value.filter((row) => allowPersonnelSelection || visibleWorkerIds.has(row.workerId))
        : []);
      setContacts(contactRows.status === 'fulfilled' ? contactRows.value : []);
      setIncidents(incidentRows.status === 'fulfilled' ? incidentRows.value : []);
      setToolboxTalks(toolboxRows.status === 'fulfilled' ? toolboxRows.value : []);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load supervisor Field Mode.');
    } finally {
      setLoading(false);
    }
  }, [allowPersonnelSelection, effectiveProjectId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      void load();
    }, 15000);
    return () => window.clearInterval(interval);
  }, [load]);

  useEffect(() => {
    if (!currentTask) return undefined;
    const liveStatuses = new Set(['READY', 'IN_PROGRESS', 'PAUSED', 'INSPECTION_PENDING', 'INSPECTION_FAILED']);
    if (!liveStatuses.has(statusKey(currentTask))) return undefined;

    let cancelled = false;
    const refreshCurrentTask = async () => {
      try {
        const latest = await tasksService.getTaskById(currentTask.id);
        if (cancelled) return;
        setTasks((current) => current.map((task) => (task.id === latest.id ? latest : task)));
      } catch {
        // Keep the broader supervisor poll responsible for surfacing load errors.
      }
    };

    void refreshCurrentTask();
    const interval = window.setInterval(refreshCurrentTask, 3000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [currentTask?.id, currentTask?.lifecycleStatus]);

  if (!effectiveProjectId) {
    return projectsLoading
      ? null
      : <Alert severity="warning">No project is available for Supervisor Field Mode.</Alert>;
  }

  const openSelectedPersonnel = (personnelId: string) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('view', 'supervisor');
    nextParams.set(ACCOUNTS_PERSONNEL_QUERY_KEY, personnelId);
    setSearchParams(nextParams);
  };

  const clearSelectedPersonnel = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('view', 'supervisor');
    nextParams.delete(ACCOUNTS_PERSONNEL_QUERY_KEY);
    setSearchParams(nextParams);
  };

  const personnelIndicatorGrid = (
    <Stack spacing={1.5}>
      <Typography variant="h4" fontWeight={900}>Supervisors</Typography>
      <Grid container spacing={1.5}>
        {supervisorPersonnel.map((person) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={person.id}>
            <PersonnelFolderCard
              name={person.name}
              email={person.email}
              credentialHint={person.credentialHint}
              onClick={() => openSelectedPersonnel(person.id)}
            />
          </Grid>
        ))}
      </Grid>
    </Stack>
  );

  const personnelContextCard = selectedPersonnel ? (
    <Paper sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={900} textTransform="uppercase">Supervisor</Typography>
          <Typography variant="h5" fontWeight={900}>{selectedPersonnel.name}</Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
            <Chip size="small" label={`Login: ${selectedPersonnel.email}`} />
            <Chip size="small" label={selectedPersonnel.credentialHint} />
            <Chip size="small" label={selectedPersonnel.team} />
          </Stack>
        </Box>
        <Button size="small" variant="outlined" onClick={clearSelectedPersonnel} sx={{ borderRadius: 1, fontWeight: 900 }}>
          Back to Supervisors
        </Button>
      </Stack>
    </Paper>
  ) : null;

  if (normalizedView === 'home' && allowPersonnelSelection && !selectedPersonnel) {
    return (
      <Stack spacing={2.5}>
        {error && <Alert severity="error">{error}</Alert>}
        {loading && tasks.length === 0 && <Chip icon={<CircularProgress size={16} />} label="Loading supervisor screen" sx={{ alignSelf: 'flex-start' }} />}
        {personnelIndicatorGrid}
      </Stack>
    );
  }

  return (
    <Stack spacing={2.5}>
      {personnelContextCard}
      <PageTitle
        projectName={projectName}
        title={
          normalizedView === 'home' ? 'Home screen'
            : normalizedView === 'current' ? 'Current task'
              : normalizedView === 'upcoming' ? 'Upcoming task'
                : normalizedView === 'qc' ? 'Quality checklist'
                  : 'Safety & emergency'
        }
      />

      {error && <Alert severity="error">{error}</Alert>}
      {loading && tasks.length === 0 && <Chip icon={<CircularProgress size={16} />} label="Loading supervisor screen" sx={{ alignSelf: 'flex-start' }} />}

      {normalizedView === 'home' && (
        <HomeView
          tasks={tasks}
          incidents={incidents}
          attendanceRows={attendanceRows}
          workers={workers}
          user={user}
          selectedPersonnel={selectedPersonnel}
        />
      )}
      {normalizedView === 'current' && (
        currentTask
          ? taskAwaitingInspection(currentTask)
            ? <CurrentInspectionView task={currentTask} />
            : <CurrentSupervisorTaskView
                task={currentTask}
                workers={workers}
                workerUsers={workerUsers}
                attendanceRows={attendanceRows}
              />
          : !loading && <Alert severity="info">No current supervisor task is available.</Alert>
      )}
      {normalizedView === 'upcoming' && <UpcomingView tasks={tasks} currentTaskId={currentTask?.id} />}
      {normalizedView === 'qc' && (
        currentInspectionTask
          ? <QcChecklistView task={currentInspectionTask} reload={load} />
          : !loading && <Alert severity="info">No tasks are awaiting inspection.</Alert>
      )}
      {normalizedView === 'emergency' && (
        <EmergencyView
          tasks={tasks}
          contacts={contacts}
          incidents={incidents}
          toolboxTalks={toolboxTalks}
          teams={teams}
          workers={workers}
        />
      )}
    </Stack>
  );
}
